import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useD305Query } from './hooks/useD305Query';
import type { StatRow, ProductStatRow, UnitSummary } from '../../services/d305Api';
import './D305Form.css';

const D305Form: React.FC = () => {
  const { loading, vendorData, productData, unitSummary, groupCounts, queried, noData, currentType, runQuery, reset } = useD305Query();

  const [reportType, setReportType] = useState<string>('vendor');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');

  const handleQuery = () => {
    runQuery({
      report_type: reportType,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      vendor_from: vendorFrom.trim() || undefined,
      vendor_to: vendorTo.trim() || undefined,
      product_from: productFrom.trim() || undefined,
      product_to: productTo.trim() || undefined,
      dept_from: deptFrom.trim() || undefined,
      dept_to: deptTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setReportType('vendor');
    setDateFrom('');
    setDateTo('');
    setVendorFrom('');
    setVendorTo('');
    setProductFrom('');
    setProductTo('');
    setDeptFrom('');
    setDeptTo('');
    reset();
  };

  const hasData = currentType === 'vendor_product' ? productData.length > 0 : vendorData.length > 0;

  const handlePrint = () => {
    if (!hasData) return;
    const title = currentType === 'vendor_product' ? '採購單統計表（廠商產品別）' : '採購單統計表（廠商別）';
    const win = window.open('', '_blank');
    if (!win) return;
    const rows = currentType === 'vendor_product'
      ? productData.map(r => `<tr><td>${r.FD110_12}</td><td>${r.vendor_name}</td><td>${r.product_code}</td><td>${r.product_desc || ''}</td><td>${r.FD111_16}</td><td class="right">${r.total_qty?.toFixed(2)}</td><td class="right">${r.total_amt?.toFixed(2)}</td><td class="right">${r.avg_price?.toFixed(3)}</td></tr>`).join('')
      : vendorData.map(r => `<tr><td>${r.FD110_12}</td><td>${r.vendor_name}</td><td>${r.FD111_16}</td><td class="right">${r.total_qty?.toFixed(2)}</td><td class="right">${r.total_amt?.toFixed(2)}</td><td class="right">${r.avg_price?.toFixed(3)}</td></tr>`).join('');
    const headers = currentType === 'vendor_product'
      ? '<th>廠商代號</th><th>廠商名稱</th><th>產品代號</th><th>產品說明</th><th>單位</th><th>數量</th><th>金額</th><th>平均價</th>'
      : '<th>廠商代號</th><th>廠商名稱</th><th>單位</th><th>數量</th><th>金額</th><th>平均價</th>';
    const count = currentType === 'vendor_product' ? productData.length : vendorData.length;
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>
<p style="margin-top:8px">共 ${count} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const vendorColumns: ColumnsType<StatRow> = [
    { title: '廠商代號', dataIndex: 'FD110_12', key: 'FD110_12', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true, width: 150 },
    { title: '單位', dataIndex: 'FD111_16', key: 'FD111_16', width: 60 },
    { title: '合計數量', dataIndex: 'total_qty', key: 'total_qty', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '合計金額', dataIndex: 'total_amt', key: 'total_amt', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(3) },
  ];

  const productColumns: ColumnsType<ProductStatRow> = [
    { title: '廠商代號', dataIndex: 'FD110_12', key: 'FD110_12', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true, width: 130 },
    { title: '產品代號', dataIndex: 'product_code', key: 'product_code', width: 130 },
    { title: '產品說明', dataIndex: 'product_desc', key: 'product_desc', ellipsis: true, width: 160 },
    { title: '單位', dataIndex: 'FD111_16', key: 'FD111_16', width: 60 },
    { title: '合計數量', dataIndex: 'total_qty', key: 'total_qty', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '合計金額', dataIndex: 'total_amt', key: 'total_amt', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(3) },
  ];

  const summaryColumns: ColumnsType<UnitSummary> = [
    { title: '單位', dataIndex: 'FD111_16', key: 'FD111_16', width: 80 },
    { title: '合計數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '合計金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', align: 'right' as const, render: (v: number) => v?.toFixed(3) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.D305} action="query">
      <div className="d305-container">
        <div className="d305-header">
          <h2>採購單統計表</h2>
          {queried && !noData && hasData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio.Button value="vendor">廠商別</Radio.Button>
              <Radio.Button value="vendor_product">廠商產品別</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('採購日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('採購日期迄 (YYYY-MM-DD)')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('廠商代號起')}
              <Input value={vendorFrom} onChange={e => setVendorFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('廠商代號迄')}
              <Input value={vendorTo} onChange={e => setVendorTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號起')}
              <Input value={productFrom} onChange={e => setProductFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('產品代號迄')}
              <Input value={productTo} onChange={e => setProductTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號起')}
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號迄')}
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && hasData && (
          <>
            {groupCounts.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                廠商筆數：{groupCounts.map(g => `${g.name || g.code}(${g.count})`).join('、')}
              </div>
            )}
            {currentType === 'vendor_product' ? (
              <Table<ProductStatRow>
                columns={productColumns}
                dataSource={productData}
                rowKey={(r, i) => `${r.FD110_12}-${r.product_code}-${r.FD111_16}-${i}`}
                loading={loading}
                size="small"
                pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
                scroll={{ x: 1000 }}
                style={{ marginBottom: 16 }}
              />
            ) : (
              <Table<StatRow>
                columns={vendorColumns}
                dataSource={vendorData}
                rowKey={(r, i) => `${r.FD110_12}-${r.FD111_16}-${i}`}
                loading={loading}
                size="small"
                pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
                scroll={{ x: 700 }}
                style={{ marginBottom: 16 }}
              />
            )}
            {unitSummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>單位合計</div>
                <Table<UnitSummary>
                  columns={summaryColumns}
                  dataSource={unitSummary}
                  rowKey="FD111_16"
                  size="small"
                  pagination={false}
                  scroll={{ x: 500 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default D305Form;
